import type {
  PDF,
  PDFPageTextContent,
  PDFPageTextGlyph,
} from '@affine/core/modules/pdf';
import { firstValueFrom } from 'rxjs';

import type { PDFFindCoverage, PDFPageSearchHit } from './pdf-find-controller';

export interface LocalPdfFindOptions {
  attachmentBlockId: string;
  docId: string;
  pages: PDFPageTextContent[];
  query: string;
}

export interface LocalPdfFindProvider {
  getPageText(pageIndex: number): Promise<PDFPageTextContent>;
}

export interface LocalPdfFindRunOptions {
  attachmentBlockId: string;
  docId: string;
  pageCount: number;
  provider: LocalPdfFindProvider;
  query: string;
  signal?: AbortSignal;
  startPageIndex?: number;
}

export interface LocalPdfFindUpdate {
  coverage: PDFFindCoverage;
  hits: PDFPageSearchHit[];
}

type SearchableGlyphText = {
  glyphIndexBySearchChar: number[];
  searchableText: string;
};

export class PdfDocumentTextProvider implements LocalPdfFindProvider {
  private readonly pageTextCache = new Map<
    number,
    Promise<PDFPageTextContent>
  >();

  constructor(private readonly pdf: PDF) {}

  getPageText(pageIndex: number): Promise<PDFPageTextContent> {
    const existing = this.pageTextCache.get(pageIndex);
    if (existing) return existing;

    const promise = firstValueFrom(
      this.pdf.renderer.ob$('text', { pageNum: pageIndex })
    ).catch(error => {
      this.pageTextCache.delete(pageIndex);
      throw error;
    });
    this.pageTextCache.set(pageIndex, promise);
    return promise;
  }
}

export function normalizePdfSearchText(value: string) {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase()
    .replace(/\s+/g, '');
}

export function createSearchableGlyphText(
  glyphs: PDFPageTextGlyph[]
): SearchableGlyphText {
  const glyphIndexBySearchChar: number[] = [];
  let searchableText = '';

  glyphs.forEach((glyph, glyphIndex) => {
    const normalizedGlyph = normalizePdfSearchText(glyph.text);
    if (!normalizedGlyph) return;
    for (const char of normalizedGlyph) {
      glyphIndexBySearchChar.push(glyphIndex);
      searchableText += char;
    }
  });

  return { glyphIndexBySearchChar, searchableText };
}

export function createGlyphRangeRects(glyphs: PDFPageTextGlyph[]) {
  type Line = {
    centerY: number;
    height: number;
    glyphs: PDFPageTextGlyph[];
  };
  const lines: Line[] = [];

  for (const glyph of glyphs) {
    const centerY = glyph.top + glyph.height / 2;
    const line = lines.find(
      line =>
        Math.abs(line.centerY - centerY) <= Math.max(0.002, line.height * 0.7)
    );
    if (line) {
      line.glyphs.push(glyph);
      line.centerY =
        (line.centerY * (line.glyphs.length - 1) + centerY) /
        line.glyphs.length;
      line.height = Math.max(line.height, glyph.height);
    } else {
      lines.push({ centerY, height: glyph.height, glyphs: [glyph] });
    }
  }

  return lines.flatMap(line => {
    const sorted = [...line.glyphs].sort((a, b) => a.left - b.left);
    const groups: PDFPageTextGlyph[][] = [];
    for (const glyph of sorted) {
      const current = groups[groups.length - 1];
      const previous = current?.[current.length - 1];
      const gap = previous ? glyph.left - (previous.left + previous.width) : 0;
      if (!current || gap > Math.max(0.016, line.height * 1.4)) {
        groups.push([glyph]);
      } else {
        current.push(glyph);
      }
    }

    return groups.map(group => {
      const left = Math.min(...group.map(glyph => glyph.left));
      const top = Math.min(...group.map(glyph => glyph.top));
      const right = Math.max(...group.map(glyph => glyph.left + glyph.width));
      const bottom = Math.max(...group.map(glyph => glyph.top + glyph.height));
      const paddingX = Math.min(0.003, Math.max(0.001, (right - left) * 0.08));
      const paddingY = Math.min(0.002, Math.max(0.001, (bottom - top) * 0.08));

      return {
        x: clampUnit(left - paddingX),
        y: clampUnit(top - paddingY),
        width: clampUnit(right - left + paddingX * 2),
        height: clampUnit(bottom - top + paddingY * 2),
      };
    });
  });
}

export function localPdfFind({
  attachmentBlockId,
  docId,
  pages,
  query,
}: LocalPdfFindOptions): PDFPageSearchHit[] {
  const normalizedQuery = normalizePdfSearchText(query);
  if (!normalizedQuery) return [];

  return pages
    .flatMap(page => {
      const { glyphIndexBySearchChar, searchableText } =
        createSearchableGlyphText(page.glyphs);
      const hits: PDFPageSearchHit[] = [];
      let offset = 0;

      while (offset <= searchableText.length - normalizedQuery.length) {
        const matchStart = searchableText.indexOf(normalizedQuery, offset);
        if (matchStart === -1) break;
        const matchEnd = matchStart + normalizedQuery.length;
        const glyphIndexes = Array.from(
          new Set(glyphIndexBySearchChar.slice(matchStart, matchEnd))
        );
        const glyphs = glyphIndexes
          .map(index => page.glyphs[index])
          .filter((glyph): glyph is PDFPageTextGlyph => Boolean(glyph));

        hits.push({
          attachmentBlockId,
          coverageStatus: null,
          docId,
          pageIndex: page.pageNum,
          rects: createGlyphRangeRects(glyphs),
          score: 1,
          snippet: createSnippet(page.glyphs, glyphIndexes, query),
          source: 'text-layer',
        });
        offset = matchStart + Math.max(1, normalizedQuery.length);
      }

      return hits;
    })
    .sort((a, b) => a.pageIndex - b.pageIndex || b.score - a.score);
}

export async function* runLocalPdfFind({
  attachmentBlockId,
  docId,
  pageCount,
  provider,
  query,
  signal,
  startPageIndex = 0,
}: LocalPdfFindRunOptions): AsyncGenerator<LocalPdfFindUpdate> {
  const pageOrder = createPageSearchOrder(pageCount, startPageIndex);
  const pages = new Map<number, PDFPageTextContent>();
  const searchedPageIndexes = new Set<number>();
  const textLayerPageIndexes = new Set<number>();
  const emptyTextLayerPageIndexes = new Set<number>();

  for (const pageIndex of pageOrder) {
    throwIfAborted(signal);
    const pageText = await provider.getPageText(pageIndex);
    throwIfAborted(signal);
    pages.set(pageIndex, pageText);
    searchedPageIndexes.add(pageIndex);
    if (pageText.glyphs.length > 0) {
      textLayerPageIndexes.add(pageIndex);
    } else {
      emptyTextLayerPageIndexes.add(pageIndex);
    }

    const complete = searchedPageIndexes.size === pageCount;
    yield {
      coverage: createCoverage({
        complete,
        emptyTextLayerPageIndexes,
        pageCount,
        scanning: !complete,
        searchedPageIndexes,
        textLayerPageIndexes,
      }),
      hits: localPdfFind({
        attachmentBlockId,
        docId,
        pages: [...pages.values()],
        query,
      }),
    };
  }

  if (pageCount === 0) {
    yield {
      coverage: createCoverage({
        complete: true,
        emptyTextLayerPageIndexes,
        pageCount,
        scanning: false,
        searchedPageIndexes,
        textLayerPageIndexes,
      }),
      hits: [],
    };
  }
}

function createCoverage({
  complete,
  emptyTextLayerPageIndexes,
  pageCount,
  scanning,
  searchedPageIndexes,
  textLayerPageIndexes,
}: {
  complete: boolean;
  emptyTextLayerPageIndexes: Set<number>;
  pageCount: number;
  scanning: boolean;
  searchedPageIndexes: Set<number>;
  textLayerPageIndexes: Set<number>;
}): PDFFindCoverage {
  return {
    capReached: false,
    complete,
    emptyTextLayerPageIndexes: [...emptyTextLayerPageIndexes].sort(
      (a, b) => a - b
    ),
    scanning,
    searchedPageIndexes: [...searchedPageIndexes].sort((a, b) => a - b),
    textLayerPageIndexes: [...textLayerPageIndexes].sort((a, b) => a - b),
    totalPageCount: pageCount,
  };
}

function createPageSearchOrder(pageCount: number, startPageIndex: number) {
  if (pageCount <= 0) return [];
  const normalizedStart = Math.min(Math.max(0, startPageIndex), pageCount - 1);
  const order: number[] = [];
  const seen = new Set<number>();
  const push = (pageIndex: number) => {
    if (pageIndex < 0 || pageIndex >= pageCount) return;
    if (seen.has(pageIndex)) return;
    seen.add(pageIndex);
    order.push(pageIndex);
  };

  push(normalizedStart);
  push(normalizedStart + 1);
  push(normalizedStart - 1);
  for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
    push(pageIndex);
  }
  return order;
}

function createSnippet(
  glyphs: PDFPageTextGlyph[],
  glyphIndexes: number[],
  fallback: string
) {
  if (glyphIndexes.length === 0) return fallback;
  const start = Math.max(0, Math.min(...glyphIndexes) - 12);
  const end = Math.min(glyphs.length, Math.max(...glyphIndexes) + 13);
  const snippet = glyphs
    .slice(start, end)
    .map(glyph => glyph.text)
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
  return snippet || fallback;
}

function throwIfAborted(signal?: AbortSignal) {
  if (!signal?.aborted) return;
  throw new DOMException('PDF find aborted', 'AbortError');
}

function clampUnit(value: number) {
  return Math.max(0, Math.min(1, value));
}
